import { useEffect, useState } from "react";

import { Box, Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import { AssetDistributionRejectionLog } from "../../../config/types";
import useAppNavigation from "../../../hooks/useAppNavigation";
import { MainPageBase } from "../../page-bases/MainPageBase";
import LabelButton from "../../../components/Button/LabelButton";
import appRoutePath from "../../../config/path";
import SimpleLabel from "../../../components/Label";
import CustomAccordion from "../../../components/custom-accordion";
import { distributionRejectionApi } from "../../../services/distribution/distribution-rejection";

const DistributionRejectionDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<AssetDistributionRejectionLog | null>();


  const fetchData = async () => {
    const res = await distributionRejectionApi.getRejectedDistributionById(
      id ?? ""
    );
    if (res.data) {
      setData(res.data);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useAppNavigation();

  if (!data) {
    return null;
  }

  return (
    <MainPageBase
      pageState="main"
      title="Asset Rejection Information"
      extraComponent={
        <LabelButton
          onPress={() =>
            navigate({
              path: appRoutePath.REJECTED_INBOUND_ASSET_DISTRIBUTIONS_PAGE,
            })
          }
          label="Back"
          direction="left"
        />
      }
    >
      <Box height="100%" display="flex" justifyContent="center" >
        <Stack width="100%">
          <Stack spacing={1.5} mb={2}>
            {/* Asset Information */}
            <Stack spacing={1}>
              <SimpleLabel title="Asset Information" />
              <Stack direction="row" width="100%">
                <SimpleLabel title="Brand Name" fontWeight={400} />
                <SimpleLabel
                  fontWeight={700}
                  title={data.asset_brand}
                  width="100%"
                />
              </Stack>
              <Stack direction="row" width="100%">
                <SimpleLabel title="Model Name" fontWeight={400} />
                <SimpleLabel
                  fontWeight={700}
                  title={data.asset_model}
                  width="100%"
                />
              </Stack>
              <Stack direction="row" width="100%">
                <SimpleLabel title="Asset Embossment Number" fontWeight={400} />
                <SimpleLabel
                  fontWeight={700}
                  title={data.asset_embossment_number}
                  width="100%"
                />
              </Stack>
              <Stack direction="row" width="100%">
                <SimpleLabel title="Asset Serial Number" fontWeight={400} />
                <SimpleLabel
                  fontWeight={700}
                  title={data.asset_serial_number}
                  width="100%"
                />
              </Stack>
            </Stack>

            {/* Distribution Batch Information */}
            <Stack spacing={1}>
              <SimpleLabel title="Distribution Batch Details" />
              <Stack direction="row" width="100%">
                <SimpleLabel title="Batch Number" fontWeight={400} />
                <SimpleLabel
                  fontWeight={700}
                  title={data.distribution_batch_number}
                  width="100%"
                />
              </Stack>
              <Stack direction="row" width="100%">
                <SimpleLabel title="Batch Status" fontWeight={400} />
                <SimpleLabel
                  fontWeight={700}
                  title={data.distribution_batch_status}
                  width="100%"
                />
              </Stack>
              <Stack direction="row" width="100%">
                <SimpleLabel title="Batch Date" fontWeight={400} />
                <SimpleLabel
                  fontWeight={700}
                  title={new Date(
                    data.distribution_batch_date
                  ).toLocaleString()}
                  width="100%"
                />
              </Stack>
              <Stack direction="row" width="100%">
                <SimpleLabel title="From - Domain (Location)" fontWeight={400} />
                <SimpleLabel
                  fontWeight={700}
                  title={`${data.from_location_domain} ( ${data.from_location} )`}
                  width="100%"
                />
              </Stack>

              <Stack direction="row" width="100%">
                <SimpleLabel title="To - Domain (Location)" fontWeight={400} />
                <SimpleLabel
                  fontWeight={700}
                  title={`${data.to_location_domain} ( ${data.to_location} )`}
                  width="100%"
                />
              </Stack>
            </Stack>

            {/* Assigned User Information */}
            <Stack spacing={1}>
              <SimpleLabel title="Assigned User Details" />
              <Stack direction="row" width="100%">
                <SimpleLabel title="Full Name" fontWeight={400} />
                <SimpleLabel
                  fontWeight={700}
                  title={`${data.user_first_name} ${data.user_last_name}`}
                  width="100%"
                />
              </Stack>
              <Stack direction="row" width="100%">
                <SimpleLabel title="Email" fontWeight={400} />
                <SimpleLabel
                  fontWeight={700}
                  title={data.user_email}
                  width="100%"
                />
              </Stack>
              <Stack direction="row" width="100%">
                <SimpleLabel title="Telephone" fontWeight={400} />
                <SimpleLabel
                  fontWeight={700}
                  title={data.user_telephone}
                  width="100%"
                />
              </Stack>
            </Stack>

            {/* Extra Info */}
            <Stack>
              <SimpleLabel title="Extra Information" />
              <Stack direction="row" width="100%">
                <SimpleLabel title="Date Recorded" fontWeight={400} />
                <SimpleLabel
                  fontWeight={700}
                  title={new Date(data.created_at).toLocaleString()}
                  width="100%"
                />
              </Stack>
            </Stack>

            {/* Rejection Reason */}
            <CustomAccordion
              title="Rejection Reason"
              info={data.reason ?? ""}
            />
          </Stack>
        </Stack>
      </Box>
    </MainPageBase>
  );
};

export { DistributionRejectionDetailPage };
